// API base URL configuration
// import axios from 'axios';

export const API_BASE_URL = 'https://api.ibrahim-magdy.com/api';

// Multi-tenant configuration
export const TENANT_ID = 'tenant15';

// Memory storage for auth data
class AuthService {
  private userData: any = null;
  private refreshPromise: Promise<boolean> | null = null;
  private authChangeListeners: (() => void)[] = [];
  private lastRefreshAttempt: number = 0;
  private readonly minRefreshInterval: number = 5000; // 5 seconds minimum between refresh attempts

  constructor() {
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        this.userData = parsedAuth.userData;
      } catch (error) {
        console.error('Failed to parse saved auth data');
        this.clearAuthData();
      }
    }
  }

  setAuthData(token: string, userData: any): void {
    if (!token) {
      console.error('Attempted to set null or undefined token');
      return;
    }
    this.userData = userData;
    const authData = {
      token,
      userData,
      timestamp: Date.now()
    };
    localStorage.setItem('authData', JSON.stringify(authData));
    localStorage.setItem('isAuthenticated', 'true');
    this.notifyAuthChange();
  }

  getToken(): string | null {
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        return parsedAuth.token || null;
      } catch (error) {
        console.error('Failed to parse saved auth data when getting token');
        this.clearAuthData();
        return null;
      }
    }
    return null;
  }

  getUserData(): any {
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        return parsedAuth.userData || null;
      } catch (error) {
        console.error('Failed to parse saved auth data when getting user data');
        this.clearAuthData();
        return null;
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    // Check if token is too old (more than 23 hours - giving 1 hour buffer for refresh)
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        const tokenAge = Date.now() - (parsedAuth.timestamp || 0);
        const maxAge = 23 * 60 * 60 * 1000; // 23 hours in milliseconds
        
        if (tokenAge > maxAge) {
          console.log('Token too old, clearing auth data');
          this.clearAuthData();
          return false;
        }
      } catch (error) {
        console.error('Failed to check token age:', error);
        this.clearAuthData();
        return false;
      }
    }
    
    return true;
  }

  async logout(): Promise<void> {
    try {
      console.log('AuthService: Starting logout API call...');
      // Send logout request to clear the HttpOnly cookie
      await fetch(`${API_BASE_URL}/ModeratorAuth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': this.getToken() ? `Bearer ${this.getToken()}` : '',
          'X-Tenant-ID': TENANT_ID
        }
      });
      console.log('AuthService: Logout API call completed');
    } catch (error) {
      console.error('AuthService: Logout API call failed:', error);
    } finally {
      // Always clear auth data regardless of API call success
      console.log('AuthService: Clearing auth data...');
      this.clearAuthData();
      console.log('AuthService: Auth data cleared');
    }
  }

  private clearAuthData(): void {
    console.log('AuthService: Clearing user data and refresh promise...');
    this.userData = null;
    this.refreshPromise = null;
    console.log('AuthService: Removing localStorage items...');
    localStorage.removeItem('authData');
    localStorage.removeItem('isAuthenticated');
    console.log('AuthService: Notifying auth change listeners...');
    this.notifyAuthChange();
    console.log('AuthService: Auth data clear completed');
  }

  addAuthChangeListener(listener: () => void): void {
    this.authChangeListeners.push(listener);
  }

  removeAuthChangeListener(listener: () => void): void {
    this.authChangeListeners = this.authChangeListeners.filter(l => l !== listener);
  }

  private notifyAuthChange(): void {
    this.authChangeListeners.forEach(listener => listener());
  }

  async refreshToken(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Check rate limit
    const now = Date.now();
    if (now - this.lastRefreshAttempt < this.minRefreshInterval) {
      console.log('Refresh rate limited');
      return false;
    }

    this.lastRefreshAttempt = now;
    this.refreshPromise = this.performTokenRefresh();
    const result = await this.refreshPromise;
    
    // Clear the promise after completion
    this.refreshPromise = null;
    
    return result;
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      console.log('Attempting to refresh token...');
      
      const refreshResponse = await fetch(`${API_BASE_URL}/ModeratorAuth/refresh-token`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Tenant-ID': TENANT_ID,
        },
      });
      
      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        console.log('Refresh response:', refreshData);
        
        // Extract token from response structure
        if (refreshData?.success && refreshData?.data?.token) {
          const newToken = refreshData.data.token;
          const newUserData = refreshData.data.moderator;
          
          console.log('New token obtained, updating auth data...');
          this.setAuthData(newToken, newUserData || this.getUserData());
          
          return true;
        } else {
          console.error('No token found in refresh response:', refreshData);
          return false;
        }
      } else if (refreshResponse.status === 400) {
        console.error('Refresh token is invalid or expired');
        this.clearAuthData();
        window.location.href = '/login';
        return false;
      } else {
        console.error('Refresh token request failed:', refreshResponse.status, refreshResponse.statusText);
        return false;
      }
    } catch (error) {
      console.error('Error during token refresh:', error);
      return false;
    }
  }
}

export const authService = new AuthService();

export async function authFetch(input: RequestInfo, init: RequestInit = {}, retry = true): Promise<Response> {
  let token = authService.getToken();
  let headers = new Headers(init.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  // Add tenant ID header for multi-tenant support
  headers.set('X-Tenant-ID', TENANT_ID);
  
  // Always send cookies for refresh
  const fetchOptions: RequestInit = {
    ...init,
    headers,
    credentials: 'include',
  };
  
  let response = await fetch(input, fetchOptions);
  
  if (response.status === 401 && retry) {
    console.log('Token expired, attempting to refresh...');
    
    const refreshSuccess = await authService.refreshToken();
    
    if (refreshSuccess) {
      // Retry original request with new token
      const newToken = authService.getToken();
      headers.set('Authorization', `Bearer ${newToken}`);
      headers.set('X-Tenant-ID', TENANT_ID);
      const retryOptions: RequestInit = {
        ...init,
        headers,
        credentials: 'include',
      };
      
      return fetch(input, retryOptions);
    } else {
      // If refresh fails, logout and redirect
      console.log('Token refresh failed, logging out...');
      await authService.logout();
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }
  }
  
  return response;
}

export const apiUrl = (path: string): string => {
  if (path.startsWith('/')) path = path.slice(1);
  return `${API_BASE_URL}/${path}`;
};

// Helper function to get full image URL
export const getImageUrl = (imagePath: string | null | undefined): string | null => {
  if (!imagePath) return null;
  
  // If the path already starts with http/https, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Construct the base URL for images by removing /api from the end
  const baseUrl = 'https://api.ibrahim-magdy.com';
  
  // Ensure the imagePath starts with /
  const path = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  
  return `${baseUrl}${path}`;
};

export type LessonKind = 'video' | 'quiz' | 'file';

/** Maps API lesson type (name or numeric enum: Video=0, Quiz=1, File=2) to a stable kind. */
export function normalizeLessonType(raw: unknown): LessonKind {
  if (raw === null || raw === undefined) return 'video';
  if (typeof raw === 'number') {
    if (raw === 1) return 'quiz';
    if (raw === 2) return 'file';
    return 'video';
  }
  const value = String(raw).trim().toLowerCase();
  if (value === '1' || value === 'quiz') return 'quiz';
  if (value === '2' || value === 'file' || value === 'pdf') return 'file';
  return 'video';
}

export function toApiLessonType(raw: unknown): string {
  const kind = normalizeLessonType(raw);
  if (kind === 'file') return 'File';
  return kind;
}

// Helper function to get full file URL (for PDFs and other files)
export const getFileUrl = (filePath: string | null | undefined): string | null => {
  if (!filePath) return null;

  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  const baseUrl = 'https://api.ibrahim-magdy.com';
  const path = filePath.startsWith('/') ? filePath : `/${filePath}`;

  return `${baseUrl}${path}`;
};

// Books API functions
export const booksApi = {
  // Get all books (Admin only)
  getBooks: async (pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get active books
  getActiveBooks: async (pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book/active?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get books by grade (Admin only)
  getBooksByGrade: async (grade: string, pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book/grade/${grade}?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get active books by grade
  getActiveBooksbyGrade: async (grade: string, pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book/active/grade/${grade}?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get book by ID
  getBookById: async (id: string) => {
    const response = await authFetch(apiUrl(`Book/${id}`));
    return response.json();
  },

  // Create book without image
  createBook: async (bookData: any) => {
    const response = await authFetch(apiUrl('Book'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bookData),
    });
    return response.json();
  },

  // Create book with image
  createBookWithImage: async (formData: FormData) => {
    const response = await authFetch(apiUrl('Book/with-image'), {
      method: 'POST',
      body: formData,
    });
    return response.json();
  },

  // Update book without image
  updateBook: async (id: string, bookData: any) => {
    const response = await authFetch(apiUrl(`Book/${id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bookData),
    });
    return response.json();
  },

  // Update book with image
  updateBookWithImage: async (id: string, formData: FormData) => {
    const response = await authFetch(apiUrl(`Book/${id}/with-image`), {
      method: 'PUT',
      body: formData,
    });
    return response.json();
  },

  // Delete book
  deleteBook: async (id: string) => {
    const response = await authFetch(apiUrl(`Book/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },

  // Upload book image
  uploadBookImage: async (imageFile: File) => {
    const formData = new FormData();
    formData.append('imageFile', imageFile);
    
    const response = await authFetch(apiUrl('Book/upload-image'), {
      method: 'POST',
      body: formData,
    });
    return response.json();
  },
};

// Unit Codes API functions
export const unitCodesApi = {
  // Generate unit codes
  generateCodeUnit: async (unitId: number, count: number) => {
    const response = await authFetch(apiUrl(`UnitCode/GenerateCodeUnit?unitId=${unitId}&count=${count}`), {
      method: 'POST',
    });
    return response.json();
  },

  // Generate general code
  generateGeneralCode: async (unitId: number, code: string) => {
    const response = await authFetch(apiUrl(`UnitCode/GenerateGeneralCode?unitId=${unitId}&generalCode=${encodeURIComponent(code)}`), {
      method: 'POST',
    });
    return response.json();
  },

  // Get all codes
  getAllCodes: async (codeType: string, pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`UnitCode/GetAllCodes?codeType=${codeType}&pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get all general codes
  getAllGeneralCodes: async (page: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`UnitCode/GetAllGeneralCodes?page=${page}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get all codes count
  getAllCodesCount: async () => {
    const response = await authFetch(apiUrl('UnitCode/GetAllCodesCount'));
    return response.json();
  },

  // Get all student enter code
  getAllStudentEnterCode: async (pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`UnitCode/GetAllStudentEnterCode?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get all student enter code count
  getAllStudentEnterCodeCount: async () => {
    const response = await authFetch(apiUrl('UnitCode/GetAllStudentEnterCodeCount'));
    return response.text(); // Returns plain number
  },

  // Filter unit codes
  filterUnitCodes: async (filters: any) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        params.append(key, value as string);
      }
    });
    
    const response = await authFetch(apiUrl(`UnitCode/FilterUnitCodes?${params.toString()}`));
    return response.json();
  },

  // Delete code
  deleteCode: async (id: string) => {
    const response = await authFetch(apiUrl(`UnitCode/DeleteCode/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },
};

// Course tree API for getting units nested within courses
export const courseTreeApi = {
  getCourseTree: async (courseId: string) => {
    const response = await authFetch(apiUrl(`Course/tree?courseid=${courseId}`), {
      method: 'GET',
      headers: {
        'accept': '*/*'
      }
    });
    return response.json();
  }
};

// Course API functions
export const courseApi = {
  // Add course with image upload
  addCourseWithImage: async (formData: FormData) => {
    const response = await authFetch(apiUrl('Course/addWithImage'), {
      method: 'POST',
      body: formData, // Don't set Content-Type header for FormData
    });
    return response.json();
  },
};

// Question API functions
export const questionApi = {
  uploadQuestionImage: async (imageFile: File) => {
    const formData = new FormData();
    formData.append('ImageFile', imageFile);

    const response = await authFetch(apiUrl('Question/UploadQuestionImage'), {
      method: 'POST',
      body: formData,
    });
    return response.json();
  },
};

export const fileUploadApi = {
  uploadFile: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await authFetch(apiUrl('FileUpload/upload'), {
      method: 'POST',
      body: formData,
    });
    return response.json();
  },
};

// Order API functions
export const orderApi = {
  // Get all orders with filters
  getAllOrders: async (
    pageNumber: number = 1,
    pageSize: number = 10,
    status: number | null = null,
    type: number | null = null,
    studentName: string | null = null,
    studentPhoneNumber: string | null = null,
    startDate: string | null = null,
    endDate: string | null = null
  ) => {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    
    if (status !== null) params.append('status', status.toString());
    if (type !== null) params.append('type', type.toString());
    if (studentName) params.append('studentName', studentName);
    if (studentPhoneNumber) params.append('studentPhoneNumber', studentPhoneNumber);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await authFetch(apiUrl(`Order/all?${params.toString()}`));
    return response.json();
  },

  // Update order status
  updateOrderStatus: async (orderId: string, status: number) => {
    const response = await authFetch(apiUrl(`Order/${orderId}/status`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    });
    return response.json();
  },
};

// PromoCode API functions
export const promoCodesApi = {
  // Get all promo codes
  getAllPromoCodes: async (pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`PromoCode/all?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Create promo code
  createPromoCode: async (promoCodeData: any) => {
    const response = await authFetch(apiUrl('PromoCode/create'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(promoCodeData),
    });
    return response.json();
  },

  // Create promo code batch
  createPromoCodeBatch: async (batchData: any) => {
    const response = await authFetch(apiUrl('PromoCode/create-batch'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(batchData),
    });
    return response.json();
  },

  // Update promo code
  updatePromoCode: async (id: string, promoCodeData: any) => {
    const response = await authFetch(apiUrl(`PromoCode/${id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(promoCodeData),
    });
    return response.json();
  },

  // Delete promo code
  deletePromoCode: async (id: string) => {
    const response = await authFetch(apiUrl(`PromoCode/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },
};

// Transaction API functions
export const transactionApi = {
  // Get all transactions with filters
  getTransactions: async (
    pageNumber: number = 1,
    pageSize: number = 10,
    studentId: string | null = null,
    type: number | null = null,
    fromDate: string | null = null,
    toDate: string | null = null
  ) => {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    
    if (studentId) params.append('studentId', studentId);
    if (type !== null) params.append('type', type.toString());
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const response = await authFetch(apiUrl(`Transaction/all?${params.toString()}`));
    return response.json();
  },

  // Get transaction by ID
  getTransactionById: async (id: string) => {
    const response = await authFetch(apiUrl(`Transaction/${id}`));
    return response.json();
  },

  // Get student transaction history
  getStudentTransactionHistory: async (studentId: string, pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Transaction/student/${studentId}?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },
};

// Wallet Code Category API functions - matches WalletCodeCategoryController
export const walletCodeCategoryApi = {
  // Create category - POST /api/WalletCodeCategory/create
  create: async (data: {
    name: string;
    description?: string;
    price: number;
    isActive: boolean;
  }) => {
    try {
      const response = await authFetch(apiUrl('WalletCodeCategory/create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        console.error('API Error Response:', result);
        throw new Error(result.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return result;
    } catch (error: any) {
      console.error('Category create error:', error);
      throw error;
    }
  },

  // Update category - PUT /api/WalletCodeCategory/update/{id}
  update: async (
    id: string,
    data: {
      id: string;
      name: string;
      description?: string;
      price: number;
      isActive: boolean;
    }
  ) => {
    try {
      const response = await authFetch(apiUrl(`WalletCodeCategory/update/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        console.error('API Error Response:', result);
        throw new Error(result.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return result;
    } catch (error: any) {
      console.error('Category update error:', error);
      throw error;
    }
  },

  // Get category by ID - GET /api/WalletCodeCategory/{id}
  getById: async (id: string) => {
    const response = await authFetch(apiUrl(`WalletCodeCategory/${id}`));
    return response.json();
  },

  // Get all categories - GET /api/WalletCodeCategory/all
  getAll: async (isActive: boolean | null = null) => {
    const query = isActive === null ? '' : `?isActive=${isActive}`;
    const response = await authFetch(apiUrl(`WalletCodeCategory/all${query}`));
    return response.json();
  },

  // Delete category - DELETE /api/WalletCodeCategory/{id}
  delete: async (id: string) => {
    const response = await authFetch(apiUrl(`WalletCodeCategory/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },
};

// Wallet Code API functions - matches WalletCodeController
export const walletCodeApi = {
  // Create wallet codes - POST /api/WalletCode/create
  create: async (data: { categoryId: string; name: string; count: number }) => {
    const response = await authFetch(apiUrl('WalletCode/create'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Get all wallet codes with filters - GET /api/WalletCode/all
  getAll: async (params: {
    pageNumber?: number;
    pageSize?: number;
    categoryId?: string | null;
    isUsed?: boolean | null;
    searchTerm?: string | null;
    createdFrom?: string | null;
    createdTo?: string | null;
  } = {}) => {
    const query = new URLSearchParams();
    query.append('pageNumber', String(params.pageNumber ?? 1));
    query.append('pageSize', String(params.pageSize ?? 10));
    if (params.categoryId) query.append('categoryId', params.categoryId);
    if (params.isUsed !== null && params.isUsed !== undefined) {
      query.append('isUsed', String(params.isUsed));
    }
    if (params.searchTerm) query.append('searchTerm', params.searchTerm);
    if (params.createdFrom) query.append('createdFrom', params.createdFrom);
    if (params.createdTo) query.append('createdTo', params.createdTo);

    const response = await authFetch(apiUrl(`WalletCode/all?${query.toString()}`));
    return response.json();
  },

  // Get wallet code by ID - GET /api/WalletCode/{id}
  getById: async (id: string) => {
    const response = await authFetch(apiUrl(`WalletCode/${id}`));
    return response.json();
  },

  // Redeem wallet code - POST /api/WalletCode/redeem
  redeem: async (code: string) => {
    const response = await authFetch(apiUrl('WalletCode/redeem'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    return response.json();
  },

  // Delete wallet code - DELETE /api/WalletCode/{id}
  delete: async (id: string) => {
    const response = await authFetch(apiUrl(`WalletCode/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },
};